import { Box, ButtonBase, Menu, MenuItem, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useState } from "react";
import { ALPHA, BADGE, FONT_SIZE, INK, LETTER_SPACING, LEVEL_METER, MOTION, SHADOW, TOUCH_TARGET_MIN } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { portalOverlayProps } from "../Popups/portalOverlay";

export type LevelOption<T> = {
	value: T;
	/** Short label — shown in the pill and the menu row. Caller translates it. */
	label: string;
	/** One line on what this level does, shown as the pill's tooltip and under the menu label. */
	hint: string;
};

type Props<T> = {
	/** The scale, ordered lowest → highest: the position of `value` is what the meter lights up. */
	options: LevelOption<T>[];
	value: T;
	onChange: (value: T) => void;
	/** Accessible name of the whole control ("Answer depth", "Analysis effort", …). */
	ariaLabel: string;
	disabled?: boolean;
	dataTestId?: string;
};

/** The instrument glyph: `filledCount` rising bars lit in `tone`, the rest present but off. */
const LevelMeter = <T,>({ options, filledCount, tone }: { options: LevelOption<T>[]; filledCount: number; tone: string }) => (
	<Box aria-hidden sx={{ display: "flex", alignItems: "flex-end", flexShrink: 0, gap: `${LEVEL_METER.BAR_GAP}px` }}>
		{options.map((option, index) => (
			<Box
				key={option.label}
				sx={{
					width: `${LEVEL_METER.BAR_WIDTH}px`,
					height: `${LEVEL_METER.BAR_MIN_HEIGHT + index * LEVEL_METER.BAR_STEP}px`,
					borderRadius: `${LEVEL_METER.BAR_RADIUS}px`,
					backgroundColor: index < filledCount ? tone : alpha(INK.SECONDARY, ALPHA.METER_EMPTY_BAR),
					transition: `background-color ${MOTION.DURATION.CONTROL}ms ease`,
				}}
			/>
		))}
	</Box>
);

/**
 * A micro picker for an ordered low→high scale (answer depth, analysis effort, verbosity), sized to
 * live *inside* another control — a text field's adornment, a dense toolbar — instead of spending a
 * row on a labelled dropdown.
 *
 * Collapsed it is a ~24px pill: a rising-bar meter (how high the current level sits on the scale)
 * plus the level's name. Clicking opens the full scale, each level with its own meter and a one-line
 * hint, so the cheap glyph stays learnable.
 */
export const InlineLevelPicker = <T,>({ options, value, onChange, ariaLabel, disabled, dataTestId }: Props<T>) => {
	const theme = useTheme();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const isMenuOpen = menuAnchor !== null;

	const selectedIndex = options.findIndex((option) => option.value === value);
	// A value outside the scale would silently render an empty meter — the caller's option list is wrong.
	if (selectedIndex === -1) throw new Error("InlineLevelPicker received a value that is not one of its options");
	const selected = options[selectedIndex];

	const handlePick = useCallback(
		(picked: T) => {
			setMenuAnchor(null);
			onChange(picked);
		},
		[onChange],
	);

	return (
		<>
			<InHouseTooltip title={`${ariaLabel} · ${selected.hint}`} arrow placement="top">
				<ButtonBase
					data-testid={dataTestId}
					disabled={disabled}
					onClick={(event) => setMenuAnchor(event.currentTarget)}
					aria-haspopup="menu"
					aria-expanded={isMenuOpen}
					aria-label={`${ariaLabel}: ${selected.label}`}
					sx={{
						position: "relative",
						// The pill's `height` is fixed, so padding can't grow it under border-box sizing — an
						// invisible ::after extends the HIT area to 44px while the painted pill stays 24px.
						// Grows DOWNWARD only — a centered box reaches ~10px above the pill into the composer's
						// textarea and would steal those taps for the depth menu.
						"@media (pointer: coarse)": {
							"&::after": { content: '""', position: "absolute", left: 0, right: 0, top: 0, height: `${TOUCH_TARGET_MIN}px` },
						},
						height: `${LEVEL_METER.PILL_HEIGHT}px`,
						px: 0.75,
						gap: 0.625,
						flexShrink: 0,
						borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
						color: theme.palette.primary.dark,
						backgroundColor: alpha(theme.palette.primary.main, isMenuOpen ? ALPHA.CONTROL_TINT_HOVER : ALPHA.SELECTED_OVERLAY),
						transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ease, transform ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
						"&:active": { transform: "scale(0.97)" },
						"&.Mui-disabled": { opacity: 0.5 },
						"&:focus-visible": { outline: `2px solid ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`, outlineOffset: 1 },
						// Hover tint only where hovering is real — on touch the pill would otherwise stay "lit" after a tap.
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) },
						},
						"@media (prefers-reduced-motion: reduce)": { "&:active": { transform: "none" } },
					}}
				>
					<LevelMeter options={options} filledCount={selectedIndex + 1} tone={theme.palette.primary.main} />
					<Typography
						sx={{
							fontSize: FONT_SIZE.LABEL_XS,
							fontWeight: 600,
							lineHeight: 1,
							letterSpacing: LETTER_SPACING.LABEL,
							textTransform: "uppercase",
							whiteSpace: "nowrap",
						}}
					>
						{selected.label}
					</Typography>
				</ButtonBase>
			</InHouseTooltip>
			<Menu
				anchorEl={menuAnchor}
				open={isMenuOpen}
				onClose={() => setMenuAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "left" }}
				transformOrigin={{ vertical: "bottom", horizontal: "left" }}
				slotProps={{
					paper: {
						...portalOverlayProps("inline-level-picker"),
						sx: { maxWidth: `${LEVEL_METER.MENU_MAX_WIDTH}px`, boxShadow: SHADOW.POPOVER_FLOAT, borderRadius: 2 },
					},
					list: { dense: true, "aria-label": ariaLabel },
				}}
			>
				{options.map((option, index) => (
					<MenuItem
						key={option.label}
						selected={index === selectedIndex}
						onClick={() => handlePick(option.value)}
						data-testid={dataTestId ? `${dataTestId}-option-${String(option.value)}` : undefined}
						sx={{ gap: 1.25, alignItems: "center", py: 0.75, whiteSpace: "normal" }}
					>
						<LevelMeter options={options} filledCount={index + 1} tone={index === selectedIndex ? theme.palette.primary.main : INK.MUTED} />
						<Box sx={{ minWidth: 0 }}>
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: index === selectedIndex ? 600 : 500, lineHeight: 1.3 }}>{option.label}</Typography>
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.35 }}>{option.hint}</Typography>
						</Box>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};
